import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  draft: { label: 'Draft', color: 'bg-gray-100 text-gray-700 border-gray-200' },
  pending_review: { label: 'Pending Review', color: 'bg-amber-100 text-amber-700 border-amber-200' },
  approved: { label: 'Approved', color: 'bg-blue-100 text-blue-700 border-blue-200' },
  scheduled: { label: 'Scheduled', color: 'bg-purple-100 text-purple-700 border-purple-200' },
  posted: { label: 'Posted', color: 'bg-green-100 text-green-700 border-green-200' },
  failed: { label: 'Failed', color: 'bg-red-100 text-red-700 border-red-200' },
};

const PLATFORM_META = {
  instagram: { label: 'Instagram', icon: '📸' },
  tiktok: { label: 'TikTok', icon: '🎵' },
  facebook: { label: 'Facebook', icon: '👍' },
};

const emptyForm = {
  title: '',
  content_description: '',
  media_type: 'video',
  media_source: 'upload',
  media_url: '',
  platforms: ['instagram', 'tiktok'],
  caption_options: { instagram_captions: [], tiktok_captions: [] },
  hashtag_options: [],
  selected_caption: '',
  selected_hashtags: [],
  status: 'draft',
  scheduled_at: '',
};

const MediaCampaignManager = () => {
  const [posts, setPosts] = useState([]);
  const [stats, setStats] = useState({ posts_this_month: 0, pending_review: 0, scheduled: 0, total_posted: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [platformFilter, setPlatformFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [timeFilter, setTimeFilter] = useState('all'); // 'all' | 'this_month'
  const [showEditor, setShowEditor] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [genError, setGenError] = useState('');
  const [postingId, setPostingId] = useState(null);
  const [result, setResult] = useState(null); // {ok, message, permalink, title}

  const token = () => localStorage.getItem('adminToken');
  const authHeaders = () => ({ 'Authorization': `Bearer ${token()}`, 'Content-Type': 'application/json' });

  useEffect(() => { refresh(); }, []);

  const refresh = async () => {
    await Promise.all([fetchPosts(), fetchStats()]);
  };

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/campaigns`, { headers: authHeaders() });
      if (res.ok) { setPosts(await res.json()); setError(''); }
      else setError('Failed to load campaign posts');
    } catch (e) { setError('Connection error'); }
    finally { setLoading(false); }
  };

  const fetchStats = async () => {
    try {
      const res = await fetch(`${API}/api/admin/campaigns/stats`, { headers: authHeaders() });
      if (res.ok) setStats(await res.json());
    } catch (e) { /* noop */ }
  };

  const openCreate = () => { setForm(emptyForm); setEditingId(null); setGenError(''); setShowEditor(true); };

  const openEdit = (post) => {
    setForm({
      ...emptyForm,
      ...post,
      caption_options: post.caption_options || { instagram_captions: [], tiktok_captions: [] },
      hashtag_options: post.hashtag_options || [],
      selected_hashtags: post.selected_hashtags || [],
      scheduled_at: post.scheduled_at ? post.scheduled_at.slice(0, 16) : '',
    });
    setEditingId(post.id);
    setGenError('');
    setShowEditor(true);
  };

  const togglePlatform = (p) => {
    setForm((prev) => ({
      ...prev,
      platforms: prev.platforms.includes(p) ? prev.platforms.filter((x) => x !== p) : [...prev.platforms, p],
    }));
  };

  const toggleHashtag = (tag) => {
    setForm((prev) => ({
      ...prev,
      selected_hashtags: prev.selected_hashtags.includes(tag)
        ? prev.selected_hashtags.filter((t) => t !== tag)
        : [...prev.selected_hashtags, tag],
    }));
  };

  const generateAICaptions = async () => {
    if (!form.content_description.trim()) { setGenError('Please describe the content first so AI can write captions.'); return; }
    setGenError('');
    setGenerating(true);
    try {
      const res = await fetch(`${API}/api/admin/campaigns/generate-captions`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ description: form.content_description, platforms: form.platforms }),
      });
      const data = await res.json();
      if (res.ok) {
        setForm((prev) => ({
          ...prev,
          caption_options: { instagram_captions: data.instagram_captions || [], tiktok_captions: data.tiktok_captions || [] },
          hashtag_options: data.hashtags || [],
          selected_hashtags: data.hashtags || [],
        }));
      } else setGenError(data.detail || 'Failed to generate captions');
    } catch (e) { setGenError('Connection error while generating captions'); }
    finally { setGenerating(false); }
  };

  const savePost = async () => {
    if (!form.title.trim()) { setGenError('Please add a title.'); return; }
    setSaving(true);
    const payload = { ...form, scheduled_at: form.scheduled_at ? new Date(form.scheduled_at).toISOString() : null };
    try {
      const url = editingId ? `${API}/api/admin/campaigns/${editingId}` : `${API}/api/admin/campaigns`;
      const res = await fetch(url, { method: editingId ? 'PUT' : 'POST', headers: authHeaders(), body: JSON.stringify(payload) });
      if (res.ok) { setShowEditor(false); refresh(); }
      else { const d = await res.json(); setGenError(d.detail || 'Failed to save post'); }
    } catch (e) { setGenError('Connection error while saving'); }
    finally { setSaving(false); }
  };

  const changeStatus = async (post, status) => {
    try {
      const res = await fetch(`${API}/api/admin/campaigns/${post.id}`, {
        method: 'PUT', headers: authHeaders(), body: JSON.stringify({ status }),
      });
      if (res.ok) refresh();
    } catch (e) { /* noop */ }
  };

  const deletePost = async (id) => {
    if (!window.confirm('Delete this campaign post? This cannot be undone.')) return;
    try {
      const res = await fetch(`${API}/api/admin/campaigns/${id}`, { method: 'DELETE', headers: authHeaders() });
      if (res.ok) refresh();
    } catch (e) { /* noop */ }
  };

  const postNow = async (post) => {
    // Instagram-only auto-posting this phase. Posts not targeting Instagram are marked posted manually.
    if (!(post.platforms || []).includes('instagram')) {
      changeStatus(post, 'posted');
      return;
    }
    setPostingId(post.id);
    setResult(null);
    try {
      const res = await fetch(`${API}/api/admin/campaigns/${post.id}/post-instagram`, { method: 'POST', headers: authHeaders() });
      const data = await res.json();
      if (res.ok && data.success) {
        setResult({ ok: true, title: post.title, message: 'Published to Instagram successfully.', permalink: data.permalink });
      } else {
        const detail = data.detail;
        const msg = typeof detail === 'string' ? detail : (detail?.message || 'Posting failed');
        setResult({ ok: false, title: post.title, message: msg });
      }
    } catch (e) {
      setResult({ ok: false, title: post.title, message: 'Connection error while posting' });
    } finally {
      setPostingId(null);
      refresh();
    }
  };

  const inThisMonth = (iso) => {
    if (!iso) return false;
    const d = new Date(iso); const now = new Date();
    return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
  };

  const filtered = posts.filter((p) => {
    if (platformFilter !== 'all' && !(p.platforms || []).includes(platformFilter)) return false;
    if (statusFilter !== 'all' && p.status !== statusFilter) return false;
    if (timeFilter === 'this_month' && !inThisMonth(p.created_at)) return false;
    return true;
  });

  const fmtScheduled = (iso) => {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleString(undefined, { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  };
  const fmtPosted = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
  };

  return (
    <div className="max-w-6xl" data-testid="media-campaign-manager">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Media Campaign Manager</h1>
          <p className="text-gray-500 text-sm mt-1">Plan, write & schedule your Instagram, TikTok and Facebook content.</p>
        </div>
        <button onClick={openCreate} data-testid="new-campaign-btn"
          className="inline-flex items-center justify-center px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors">
          + Add New Content
        </button>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard label="Posts this month" value={stats.posts_this_month} testid="stat-posts-this-month" accent="text-gray-900" />
        <StatCard label="Pending approval" value={stats.pending_review} testid="stat-pending-review" accent="text-amber-600" />
        <StatCard label="Scheduled" value={stats.scheduled} testid="stat-scheduled" accent="text-purple-600" />
        <StatCard label="Total posted" value={stats.total_posted} testid="stat-total-posted" accent="text-green-600" />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="flex flex-wrap gap-1.5">
          {['all', 'instagram', 'tiktok', 'facebook'].map((p) => (
            <button key={p} onClick={() => setPlatformFilter(p)} data-testid={`platform-filter-${p}`}
              className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${platformFilter === p ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'}`}>
              {p === 'all' ? 'All' : PLATFORM_META[p].label}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap gap-2 ml-auto">
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="status-filter-dropdown"
            className="px-3 py-1.5 text-sm rounded-full border border-gray-200 bg-white text-gray-600">
            <option value="all">All statuses</option>
            {Object.entries(STATUS_META).map(([k, v]) => (<option key={k} value={k}>{v.label}</option>))}
          </select>
          <button onClick={() => setTimeFilter(timeFilter === 'this_month' ? 'all' : 'this_month')} data-testid="time-filter-toggle"
            className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${timeFilter === 'this_month' ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'}`}>
            {timeFilter === 'this_month' ? 'This month' : 'All time'}
          </button>
        </div>
      </div>

      {error && <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm">{error}</div>}

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" /></div>
        ) : filtered.length === 0 ? (
          <EmptyState onCreate={openCreate} hasPosts={posts.length > 0} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="campaign-table">
              <thead>
                <tr className="text-left text-gray-500 border-b border-gray-100">
                  <th className="px-5 py-3 font-medium">Title</th>
                  <th className="px-5 py-3 font-medium">Platform</th>
                  <th className="px-5 py-3 font-medium">Status</th>
                  <th className="px-5 py-3 font-medium">Scheduled</th>
                  <th className="px-5 py-3 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((post) => (
                  <Row key={post.id} post={post} fmtScheduled={fmtScheduled} fmtPosted={fmtPosted}
                    onEdit={openEdit} onDelete={deletePost} onStatus={changeStatus} onPostNow={postNow} postingId={postingId} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showEditor && (
        <Editor form={form} setForm={setForm} editingId={editingId} generating={generating} saving={saving}
          genError={genError} togglePlatform={togglePlatform} toggleHashtag={toggleHashtag}
          onGenerate={generateAICaptions} onSave={savePost} onClose={() => setShowEditor(false)} />
      )}

      {postingId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" data-testid="posting-overlay">
          <div className="bg-white rounded-2xl px-8 py-6 text-center shadow-2xl">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto mb-3" />
            <p className="text-sm font-medium text-gray-800">Publishing to Instagram…</p>
            <p className="text-xs text-gray-400 mt-1">Video Reels can take up to a minute to process.</p>
          </div>
        </div>
      )}

      {result && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="post-result-modal">
          <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl w-full max-w-md shadow-2xl p-6">
            <div className={`text-4xl mb-3 ${result.ok ? 'text-green-500' : 'text-red-500'}`}>{result.ok ? '✓' : '✕'}</div>
            <h3 className="text-lg font-bold text-gray-900">{result.ok ? 'Posted to Instagram' : 'Posting Failed'}</h3>
            <p className="text-sm text-gray-500 mt-1">{result.title}</p>
            <div className={`mt-3 p-3 rounded-lg text-sm ${result.ok ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`} data-testid="post-result-message">
              {result.message}
            </div>
            {result.ok && result.permalink && (
              <a href={result.permalink} target="_blank" rel="noreferrer" data-testid="post-result-link"
                className="mt-3 inline-block text-sm text-blue-600 hover:underline break-all">View live post on Instagram →</a>
            )}
            {!result.ok && (
              <p className="text-xs text-gray-400 mt-3">The post status is now "Failed" and the error was saved to its notes. Fix the issue (e.g. use a public HTTPS media URL) and Retry.</p>
            )}
            <div className="flex justify-end mt-4">
              <button onClick={() => setResult(null)} data-testid="post-result-close"
                className="px-4 py-2 bg-gray-900 hover:bg-gray-800 text-white rounded-lg text-sm font-medium">Close</button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

const StatCard = ({ label, value, testid, accent }) => (
  <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
    className="bg-white rounded-xl border border-gray-200 p-4" data-testid={testid}>
    <div className="text-xs text-gray-500 font-medium">{label}</div>
    <div className={`text-3xl font-bold mt-1 ${accent}`} data-testid={`${testid}-value`}>{value}</div>
  </motion.div>
);

const StatusBadge = ({ status }) => {
  const meta = STATUS_META[status] || STATUS_META.draft;
  return <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-medium border ${meta.color}`}>{meta.label}</span>;
};

const Row = ({ post, fmtScheduled, fmtPosted, onEdit, onDelete, onStatus, onPostNow, postingId }) => {
  const busy = postingId === post.id;
  const actions = [];
  const btn = (label, cls, fn, key) => (
    <button key={key} onClick={fn} disabled={busy} data-testid={`${key}-${post.id}`}
      className={`px-2.5 py-1 text-xs rounded-md border transition-colors disabled:opacity-50 ${cls}`}>{label}</button>
  );
  const editBtn = btn('Edit', 'border-gray-200 text-gray-600 hover:bg-gray-50', () => onEdit(post), 'edit');
  const postNowBtn = btn(busy ? 'Posting…' : 'Post Now', 'border-green-200 text-green-600 hover:bg-green-50', () => onPostNow(post), 'postnow');
  switch (post.status) {
    case 'draft':
      actions.push(editBtn, btn('Delete', 'border-red-200 text-red-600 hover:bg-red-50', () => onDelete(post.id), 'delete'));
      break;
    case 'pending_review':
      actions.push(editBtn, btn('Approve', 'border-blue-200 text-blue-600 hover:bg-blue-50', () => onStatus(post, 'approved'), 'approve'));
      break;
    case 'approved':
      actions.push(editBtn, btn('Schedule', 'border-purple-200 text-purple-600 hover:bg-purple-50', () => onEdit(post), 'schedule'), postNowBtn);
      break;
    case 'scheduled':
      actions.push(editBtn, postNowBtn);
      break;
    case 'posted':
      if (post.instagram_permalink) {
        actions.push(
          <a key="iglink" href={post.instagram_permalink} target="_blank" rel="noreferrer" data-testid={`iglink-${post.id}`}
            className="px-2.5 py-1 text-xs rounded-md border border-green-200 text-green-600 hover:bg-green-50">View on IG</a>
        );
      } else {
        actions.push(btn('View', 'border-gray-200 text-gray-600 hover:bg-gray-50', () => onEdit(post), 'view'));
      }
      break;
    case 'failed':
      actions.push(btn('Retry', 'border-amber-200 text-amber-600 hover:bg-amber-50', () => onPostNow(post), 'retry'), editBtn);
      break;
    default:
      actions.push(editBtn);
  }
  return (
    <tr className="border-b border-gray-50 hover:bg-gray-50/60 transition-colors align-top" data-testid={`campaign-row-${post.id}`}>
      <td className="px-5 py-4">
        <div className="font-semibold text-gray-900">{post.title}</div>
        <div className="text-xs text-gray-400 mt-0.5">{post.media_type === 'video' ? '🎬 Video' : '🖼️ Photo'}</div>
      </td>
      <td className="px-5 py-4">
        <div className="flex flex-wrap gap-1.5">
          {(post.platforms || []).map((p) => (
            <span key={p} className="inline-block px-2 py-0.5 rounded-md text-xs bg-gray-100 text-gray-600">{PLATFORM_META[p]?.label || p}</span>
          ))}
        </div>
      </td>
      <td className="px-5 py-4">
        <StatusBadge status={post.status} />
        {post.status === 'failed' && post.notes && (
          <div className="text-xs text-red-500 mt-1 max-w-[220px]" data-testid={`row-error-${post.id}`}>{post.notes}</div>
        )}
      </td>
      <td className="px-5 py-4 text-gray-600 whitespace-nowrap">
        {post.status === 'posted' ? fmtPosted(post.posted_at || post.scheduled_at) : fmtScheduled(post.scheduled_at)}
      </td>
      <td className="px-5 py-4">
        <div className="flex flex-wrap gap-2 justify-end">{actions}</div>
      </td>
    </tr>
  );
};

const EmptyState = ({ onCreate, hasPosts }) => (
  <div className="text-center py-20" data-testid="campaign-empty">
    <div className="text-5xl mb-3">📣</div>
    <h3 className="text-lg font-semibold text-gray-800">{hasPosts ? 'No posts match these filters' : 'No campaign posts yet'}</h3>
    <p className="text-gray-500 text-sm mt-1 mb-4">{hasPosts ? 'Try adjusting the filters above.' : 'Create your first post and let AI write the captions.'}</p>
    {!hasPosts && <button onClick={onCreate} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium">+ Add New Content</button>}
  </div>
);

const Editor = ({ form, setForm, editingId, generating, saving, genError, togglePlatform, toggleHashtag, onGenerate, onSave, onClose }) => {
  const set = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));
  const igCaps = form.caption_options?.instagram_captions || [];
  const ttCaps = form.caption_options?.tiktok_captions || [];
  const hasCaptions = igCaps.length > 0 || ttCaps.length > 0;
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [uploadedName, setUploadedName] = useState('');
  const fileInputRef = React.useRef(null);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadError('');
    setUploading(true);
    setUploadedName('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API}/api/admin/campaigns/upload`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('adminToken')}` },
        body: fd,
      });
      const data = await res.json();
      if (res.ok) {
        set('media_url', `${API}${data.url}`);
        set('media_type', data.content_type?.startsWith('video/') ? 'video' : 'photo');
        setUploadedName(data.original_filename || file.name);
      } else {
        setUploadError(typeof data.detail === 'string' ? data.detail : 'Upload failed');
      }
    } catch (err) {
      setUploadError('Connection error during upload');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 p-4 overflow-y-auto" data-testid="campaign-editor">
      <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl w-full max-w-3xl my-8 shadow-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white rounded-t-2xl z-10">
          <h2 className="text-lg font-bold text-gray-900">{editingId ? 'Edit Post' : 'New Campaign Post'}</h2>
          <button onClick={onClose} data-testid="editor-close" className="text-gray-400 hover:text-gray-700 text-xl leading-none">×</button>
        </div>

        <div className="px-6 py-5 space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
            <input type="text" value={form.title} onChange={(e) => set('title', e.target.value)} data-testid="field-title"
              placeholder="e.g. Night Dive Reel — Bioluminescence"
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Media Type</label>
              <select value={form.media_type} onChange={(e) => set('media_type', e.target.value)} data-testid="field-media-type" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white">
                <option value="video">🎬 Video</option>
                <option value="photo">🖼️ Photo</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Media Source</label>
              <select value={form.media_source} onChange={(e) => set('media_source', e.target.value)} data-testid="field-media-source" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white">
                <option value="upload">Upload processed file</option>
                <option value="drive_link">Google Drive link</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">{form.media_source === 'drive_link' ? 'Google Drive Share Link' : 'Media File'}</label>
            {form.media_source === 'upload' ? (
              <div>
                <input ref={fileInputRef} type="file" accept="video/mp4,video/quicktime,video/webm,image/jpeg,image/png,image/webp"
                  onChange={handleUpload} data-testid="field-media-upload" className="hidden" id="campaign-file-input" />
                <div className="flex flex-wrap items-center gap-3">
                  <button type="button" onClick={() => fileInputRef.current?.click()} disabled={uploading} data-testid="upload-media-btn"
                    className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 hover:bg-gray-50 disabled:opacity-60 text-gray-700 rounded-lg text-sm font-medium">
                    {uploading ? (<><span className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-500" /> Uploading…</>) : '⬆️ Upload processed file'}
                  </button>
                  {form.media_url && !uploading && (
                    <span className="text-xs text-green-600" data-testid="upload-success">✓ {uploadedName || 'File ready'}</span>
                  )}
                </div>
                {form.media_url && (
                  <input type="text" value={form.media_url || ''} readOnly data-testid="field-media-url"
                    className="w-full mt-2 border border-gray-200 rounded-lg px-3 py-2 text-xs text-gray-500 bg-gray-50" />
                )}
                {uploadError && <p className="text-xs text-red-600 mt-1" data-testid="upload-error">{uploadError}</p>}
                <p className="text-xs text-gray-400 mt-1">MP4/MOV video or JPG/PNG image. Stored securely and given a public HTTPS URL that Instagram can fetch.</p>
              </div>
            ) : (
              <div>
                <input type="text" value={form.media_url || ''} onChange={(e) => set('media_url', e.target.value)} data-testid="field-media-url"
                  placeholder="https://drive.google.com/file/d/.../view"
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
                <p className="text-xs text-amber-600 mt-1">⚠️ Google Drive links can't be posted to Instagram. For a real post, switch Media Source to "Upload processed file".</p>
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Target Platforms</label>
            <div className="flex gap-2">
              {Object.entries(PLATFORM_META).map(([k, v]) => (
                <button key={k} onClick={() => togglePlatform(k)} data-testid={`platform-${k}`}
                  className={`px-3 py-1.5 rounded-lg text-sm border transition-colors ${form.platforms.includes(k) ? 'bg-blue-50 border-blue-500 text-blue-700' : 'bg-white border-gray-200 text-gray-500'}`}>
                  {v.icon} {v.label}
                </button>
              ))}
            </div>
          </div>

          <div className="bg-gradient-to-br from-indigo-50 to-blue-50 rounded-xl p-4 border border-indigo-100">
            <label className="block text-sm font-medium text-gray-700 mb-1">Describe the content (for AI)</label>
            <textarea value={form.content_description} onChange={(e) => set('content_description', e.target.value)} data-testid="field-description"
              rows={3} placeholder="e.g. GoPro footage of a night dive — glowing plankton, divers descending with torches, a sea turtle swims past..."
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-transparent" />
            <button onClick={onGenerate} disabled={generating} data-testid="generate-captions-btn"
              className="mt-2 inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 text-white rounded-lg text-sm font-medium transition-colors">
              {generating ? (<><span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" /> Generating…</>) : '✨ Generate Captions with AI'}
            </button>
            {genError && <p className="text-xs text-red-600 mt-2" data-testid="editor-error">{genError}</p>}
          </div>

          {hasCaptions && (
            <div className="space-y-4" data-testid="caption-options">
              <CaptionGroup title="📸 Instagram Captions" caps={igCaps} selected={form.selected_caption} onSelect={(c) => set('selected_caption', c)} prefix="ig" />
              <CaptionGroup title="🎵 TikTok Captions" caps={ttCaps} selected={form.selected_caption} onSelect={(c) => set('selected_caption', c)} prefix="tt" />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Final Caption (editable)</label>
            <textarea value={form.selected_caption} onChange={(e) => set('selected_caption', e.target.value)} data-testid="field-final-caption"
              rows={3} placeholder="Pick an AI caption above, or write your own here."
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
          </div>

          {form.hashtag_options.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Hashtags <span className="text-gray-400 font-normal">(click to toggle)</span></label>
              <div className="flex flex-wrap gap-1.5" data-testid="hashtag-options">
                {form.hashtag_options.map((tag) => (
                  <button key={tag} onClick={() => toggleHashtag(tag)}
                    className={`px-2 py-1 rounded-full text-xs border transition-colors ${form.selected_hashtags.includes(tag) ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-200'}`}>{tag}</button>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">🗓️ Schedule Date/Time</label>
              <input type="datetime-local" value={form.scheduled_at} onChange={(e) => set('scheduled_at', e.target.value)} data-testid="field-schedule"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
              <select value={form.status} onChange={(e) => set('status', e.target.value)} data-testid="field-status" className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white">
                {Object.entries(STATUS_META).map(([k, v]) => (<option key={k} value={k}>{v.label}</option>))}
              </select>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-100 sticky bottom-0 bg-white rounded-b-2xl">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900">Cancel</button>
          <button onClick={onSave} disabled={saving} data-testid="save-campaign-btn"
            className="px-5 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white rounded-lg text-sm font-medium">
            {saving ? 'Saving…' : (editingId ? 'Save Changes' : 'Create Post')}
          </button>
        </div>
      </motion.div>
    </div>
  );
};

const CaptionGroup = ({ title, caps, selected, onSelect, prefix }) => {
  if (!caps || caps.length === 0) return null;
  return (
    <div>
      <h4 className="text-sm font-semibold text-gray-700 mb-2">{title}</h4>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        {caps.map((c, i) => (
          <button key={`${prefix}-${i}`} onClick={() => onSelect(c)} data-testid={`caption-${prefix}-${i}`}
            className={`text-left text-sm p-3 rounded-lg border transition-colors ${selected === c ? 'bg-blue-50 border-blue-500 ring-1 ring-blue-300' : 'bg-white border-gray-200 hover:border-gray-300'}`}>
            <div className="text-xs text-gray-400 mb-1">Option {i + 1}{selected === c ? ' — selected' : ''}</div>
            {c}
          </button>
        ))}
      </div>
    </div>
  );
};

export default MediaCampaignManager;
